import { c as _c } from "react/compiler-runtime";
import React from "react";
import { RiStickyNoteLine } from "react-icons/ri";
import { memoGateClassNames, useCalendarTextSize } from "@/lib/calendar-text-size";
import { cx } from "@/lib/tailwind-utils";

/**
 * The space left in a calendar event card, as a size container. Text inside
 * caps its height at that space rounded down to whole lines, so nothing shows
 * half cut and nothing needs measuring in script.
 */
function LeftoverSpace(t0) {
  const $ = _c(5);
  const {
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("min-h-0 flex-1 overflow-hidden text-calendar-tiny font-normal leading-tight [container-type:size]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export function InlineEventDescription(t0) {
  const $ = _c(6);
  const {
    text,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      maxHeight: "round(down, 100cqh, 1lh)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== text) {
    t2 = <div className="overflow-hidden whitespace-pre-line break-words opacity-80" style={t1}>{text}</div>;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== className || $[4] !== t2) {
    t3 = <LeftoverSpace className={className}>{t2}</LeftoverSpace>;
    $[3] = className;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Memos as small tinted notes, each led by the memo icon. The tint is the
 * card's own text colour at low strength, so it reads on every service colour
 * and in both themes. Cards too narrow for text show only the icon; cards too
 * short for a line show nothing.
 */
export function InlineEventMemos(t0) {
  const $ = _c(9);
  const {
    memos,
    className
  } = t0;
  const gates = memoGateClassNames(useCalendarTextSize());
  const t1 = gates.text;
  const t2 = cx("flex flex-col gap-px overflow-hidden text-left", gates.list);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      maxHeight: "round(down, 100cqh, 1lh)"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== memos) {
    t4 = memos.map(_temp);
    $[1] = memos;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== t4) {
    t5 = <ul className={t2} style={t3}>{t4}</ul>;
    $[3] = t2;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== gates || $[7] !== t5) {
    t6 = <div className={t1}>{t5}</div>;
    $[6] = gates;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return <LeftoverSpace className={className}><RiStickyNoteLine aria-hidden={true} className={cx("size-[0.9em] opacity-70", gates.iconOnly)} />{t6}</LeftoverSpace>;
}
function _temp(memo) {
  return <li key={memo.id} className="flex items-start gap-[3px] rounded-sm px-1" style={{
    backgroundColor: "color-mix(in srgb, currentColor 9%, transparent)"
  }}><RiStickyNoteLine aria-hidden={true} className="mt-px size-[0.9em] shrink-0 opacity-70" /><span className="min-w-0 whitespace-pre-line break-words">{memo.note}</span></li>;
}